import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DailyIframe from '@daily-co/daily-js';
import { supabase } from '../supabaseClient';
import LanguageToggle from '../components/LanguageToggle';

const NAVY     = '#0D1B2A';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// Join screen for Check-in Calls. Deliberately its own page rather than a variant of
// InterviewPage.jsx: unlike interviews, check-ins must never request or start Daily
// transcription (no candidate/job panels either, since there's no job attached). Keeping
// this page's join logic entirely separate from InterviewPage.jsx's means there is no
// shared code path that could ever pick up a startTranscription() call by accident.
export default function CheckInPage() {
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const checkinId = params.get('checkinId') || '';

  const [left, setLeft] = useState(false);
  const iframeRef    = useRef(null);
  const callFrameRef = useRef(null);

  const [loading, setLoading]     = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [checkin, setCheckin]     = useState(null);
  const [counterpartLabel, setCounterpartLabel] = useState('');
  const [myId, setMyId]           = useState(null);

  useEffect(() => {
    document.title = t('checkin.pageTitle');
    if (!document.querySelector('link[data-vw-fonts]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.setAttribute('data-vw-fonts', '1');
      link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
  }, [t]);

  useEffect(() => {
    if (!checkinId) { setLoading(false); setLoadError(true); return; }
    let cancelled = false;

    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const sessionUserId = session?.user?.id || null;
      setMyId(sessionUserId);

      const { data: ci } = await supabase.from('organization_checkins').select('*').eq('id', checkinId).single();
      if (cancelled) return;
      if (!ci) { setLoadError(true); setLoading(false); return; }
      setCheckin(ci);

      const isWorker = sessionUserId === ci.worker_id;
      if (isWorker) {
        const { data: org } = await supabase.from('organizations').select('org_name').eq('id', ci.organization_id).single();
        if (!cancelled) setCounterpartLabel(org?.org_name || '');
      } else {
        const { data: w } = await supabase.from('workers').select('first_name, last_name').eq('id', ci.worker_id).single();
        if (!cancelled) setCounterpartLabel([w?.first_name, w?.last_name].filter(Boolean).join(' '));
      }

      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [checkinId]);

  useEffect(() => {
    if (loading) return;
    const meetingLink = checkin?.meeting_link;
    if (!meetingLink || !iframeRef.current) return;

    const callFrame = DailyIframe.wrap(iframeRef.current, {
      showLeaveButton: false,
      showFullscreenButton: false,
      iframeStyle: { width: '100%', height: '100%', border: 'none' },
    });
    callFrameRef.current = callFrame;

    const joinUrl = meetingLink + (meetingLink.includes('?') ? '&' : '?') + 'dark=1';

    const handleLeftMeeting = () => setLeft(true);
    callFrame.on('left-meeting', handleLeftMeeting);

    // No startTranscription() call here, ever -- see api/create-checkin-room.js, which
    // also never sets enable_transcription:true for this call type.
    callFrame.join({ url: joinUrl }).catch(err => {
      console.error('[CheckIn] Failed to join call:', err);
    });

    return () => {
      callFrame.off('left-meeting', handleLeftMeeting);
      callFrame.destroy();
      callFrameRef.current = null;
    };
  }, [loading]); // eslint-disable-line

  const handleLeaveClick = () => {
    callFrameRef.current?.leave();
    setLeft(true);
  };

  return (
    <div style={{
      width: '100vw', height: '100dvh', overflow: 'hidden',
      backgroundColor: NAVY,
      display: 'flex', flexDirection: 'column',
      fontFamily: FONT_BODY,
    }}>
      <div style={{
        height: 56, flexShrink: 0,
        paddingTop: 'env(safe-area-inset-top)', paddingLeft: '16px', paddingRight: '16px',
        backgroundColor: '#1a2a3a',
        borderBottom: `1px solid ${AMBER}`,
        display: 'flex', alignItems: 'center', gap: '12px',
        boxSizing: 'content-box',
      }}>
        <span style={{
          fontFamily: FONT_HEAD, color: AMBER, fontSize: '16px', letterSpacing: '0.06em',
          flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {t('checkin.headerLabel')}{counterpartLabel ? ` · ${counterpartLabel}` : ''}
        </span>
        <LanguageToggle userId={myId} />
        <button
          onClick={handleLeaveClick}
          style={{
            backgroundColor: '#EF4444', color: WHITE, border: 'none', borderRadius: '20px',
            padding: '6px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          {t('checkin.leaveButton')}
        </button>
      </div>

      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0 }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 'env(safe-area-inset-bottom)' }}>
          <iframe
            ref={iframeRef}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            allow="camera; microphone; fullscreen; display-capture; autoplay"
            title={t('checkin.iframeTitle')}
          />
        </div>

        {loadError && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 6, backgroundColor: 'rgba(13,27,42,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', textAlign: 'center' }}>
            <p style={{ color: WHITE, fontSize: '15px' }}>{t('checkin.loadFailedMessage')}</p>
          </div>
        )}

        {left && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            backgroundColor: 'rgba(13,27,42,0.92)',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: '24px',
          }}>
            <p style={{ fontFamily: FONT_HEAD, color: WHITE, fontSize: '28px', letterSpacing: '2px', margin: 0, textAlign: 'center' }}>
              {t('checkin.leftMessage')}
            </p>
            <button
              onClick={() => window.close()}
              style={{
                backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)',
                border: 'none', borderRadius: '24px',
                padding: '12px 24px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
              }}
            >
              {t('checkin.closeTabButton')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
